import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as types from '../../constants/actionTypes';
import Routes from '../../constants/routes';
import { actions as baselineActions } from '../../actions/baselineActions';
import { actions as projectMgrDashboardActions } from '../../actions/projectMgrDashboardActions';
import { actions as filesActions } from '../../actions/filesActions';
import { MOBILE_WINDOW_BREAKPOINT } from '../../constants/constants';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import DashboardWidget from '../Common/DashboardWidget/DashboardWidget';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import ProjectBaselines from './ProjectBaselines';
import ProjectFiles from './ProjectFiles';
import ProjectOverview from './ProjectOverview';
import ProjectStepGuide from './ProjectStepGuide';
import './ProjectMgrDashboardPage.scss';
import DocumentTitle from 'react-document-title';
import ProjectReports from './ProjectReports';
import ProjectWaivers from './ProjectWaivers';
import Toggle from '../Common/Toggle/Toggle';

export class ProjectMgrDashboardPage extends Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			projects: [],
			selectedProject: {
				key: this.props.projectKey,
				id: 0,
			},
			isFetchingProjects: false,
			newExperienceState: false,
		};
	}

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	componentDidMount = async () => {
		await this.loadProjects();
	};

	componentDidUpdate(prevProps) {
		if (this.props.projectKey !== prevProps.projectKey) {
			const id = this.getProjectIdFromKey(this.props.projectKey, this.state.projects);

			this.setState({
				...this.state,
				selectedProject: {
					key: this.props.projectKey,
					id,
				},
			});
		}
	}

	loadProjects = async () => {
		try {
			this.setState({ isFetchingProjects: true });
			const response = await this.props.projectMgrDashboardActions.projects();
	
			if (response.type === types.LOAD_PROJECTMGR_DASHBOARD_SUMMARY_SUCCESS) {
				const projects = response.payload.projects;
				const id = this.getProjectIdFromKey(this.props.projectKey, response.payload.projects);
	
				this.setState({
					projects,
					selectedProject: {
						key: this.props.projectKey,
						id,
					},
				});
			} else if (response.type === types.LOAD_PROJECTMGR_DASHBOARD_SUMMARY_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isFetchingProjects: false });
		}
	};

	getProjectIdFromKey = (projectKey, projects) => {
		const project = projects.find(p => p.key === projectKey);
		return project ? project.id : null;
	};

	onChangeProject = (id) => {	
		const project = this.state.projects?.find((p) => p.id === id);
	
		if (!project) {
			console.error(`Project with ID ${id} not found.`);
			return;
		}
	
		this.props.history.push(Routes.Admin + '/pm-dashboard/' + project.key);
	};

	onLaunchLocationActions = () => {
		this.props.history.push(Routes.Admin + '/project/' + this.props.projectKey + '/project-locations');
	};

	onToggleChange = (name, value) => {
		this.props.history.push(Routes.NewProjectManagerDashboard + `/${this.props.projectKey}`);
	};

	render() {
		return (
			<div className='dashboard-wrapper no-gutters'>
				<DocumentTitle title='Project Dashboard' />
				<LoadingIndicator isLoading={this.state.isFetchingProjects}>
				<div className='dashboard-header'>
					<div className='row mt-3'>
						<div className='col-9'></div>
						<div className="col-3 d-flex justify-content-end">
								<Toggle
									name='isActive'
									text='New Experience'
									checked={this.state.newExperienceState}
									onChange={this.onToggleChange}
								/>
						</div>
					</div>
					</div>
					<div className='d-flex gap-3 justify-content-between dashboard-header'>
						<div className='project-selector'>
						{this.state.projects.length > 0 && (
							<MultiSelectBox
								name="projects"
								className="project-selector"
								idField="id"
								valueField="name"
								options={this.state.projects}
								isMulti={false}
								onChange={this.onChangeProject}
								selectedValues={this.state.selectedProject?.id ? String(this.state.selectedProject.id) : ""}
							/>
						)}
						</div>

						<button
							type='button'
							className='btn btn-primary no-wrap'
							onClick={this.onLaunchLocationActions}
							alt='Launch Location Actions'>
							<span>Launch Location Actions</span>
						</button>
					</div>

					<DashboardWidget bgColor='darkerGray'>
						<ProjectStepGuide
							projectMgrDashboardActions={this.props.projectMgrDashboardActions}
							subscriptionId={this.props.subscriptionId}
							projectKey={this.state.selectedProject.key}
							onNavigateToProjectLocations={this.onLaunchLocationActions}
						/>
					</DashboardWidget>

					<DashboardWidget bgColor='gray'>
						<ProjectOverview
							windowWidth={this.props.windowWidth}
							subscriptionId={this.props.subscriptionId}
							projectKey={this.state.selectedProject.key}
							projectMgrDashboardActions={this.props.projectMgrDashboardActions}
						/>
					</DashboardWidget>

					<DashboardWidget bgColor='white'>
						{this.state.selectedProject.key && (
							<ProjectBaselines
								windowWidth={this.props.windowWidth}
								subscriptionId={this.props.subscriptionId}
								projectKey={this.state.selectedProject.key}
								projectMgrDashboardActions={this.props.projectMgrDashboardActions}
								baselineActions={this.props.baselineActions}
								history={this.props.history}
							/>
						)}
					</DashboardWidget>

					<DashboardWidget bgColor='gray'>
						<ProjectReports projectId={this.state.selectedProject.id} />
					</DashboardWidget>

					<DashboardWidget bgColor='white'>
						{this.state.selectedProject.key && (
							<ProjectFiles
								windowWidth={this.props.windowWidth}
								subscriptionId={this.props.subscriptionId}
								projectKey={this.state.selectedProject.key}
								projectMgrDashboardActions={this.props.projectMgrDashboardActions}
								filesActions={this.props.filesActions}
							/>
						)}
					</DashboardWidget>

					<DashboardWidget bgColor='gray'>
						{this.state.selectedProject.id && (
							<ProjectWaivers
								projectId={this.state.selectedProject.id}
								projectKey={this.state.selectedProject.key}
								subscriptionId={this.props.subscriptionId}
								windowWidth={this.props.windowWidth}
								currentUser={this.props.user}
							/>
						)}
					</DashboardWidget>
				</LoadingIndicator>
			</div>
		);
	}
}

ProjectMgrDashboardPage.propTypes = {
	projectMgrDashboardActions: PropTypes.object.isRequired,
	baselineActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	windowWidth: PropTypes.number,
	subscriptionId: PropTypes.string.isRequired,
	projectKey: PropTypes.string.isRequired,
	history: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
	return {
		projectMgrDashboardActions: bindActionCreators(projectMgrDashboardActions, dispatch),
		baselineActions: bindActionCreators(baselineActions, dispatch),
		filesActions: bindActionCreators(filesActions, dispatch),
	};
}

function mapStateToProps(state, ownProps) {
	let projectKey = '';

	if (ownProps.match.params.projectKey) {
		projectKey = ownProps.match.params.projectKey;
	}

	return {
		windowWidth: state.window.width,
		subscriptionId: state.user.currentSubscriptionId.toString(),
		projectKey: projectKey,
		history: ownProps.history,
		user: state.user,
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectMgrDashboardPage);
